import {
  buildAddItem,
  buildBuy,
  buildDeleteTrip,
  buildEditItem,
  buildRemoveItem,
  buildRestoreItem,
  buildRestoreTrip,
  buildSeedGroceries,
  buildUndoBuy,
  defaultEstimate,
  formatPeso,
  groupByItem,
  localDate,
  newId,
  normalizeItemName,
  singletonId,
  type BuildCtx,
  type DomainEvent,
  type ItemInput,
  type Mutation,
} from '@tahi/core';
import { monotonicNow } from '../mutation';
import * as repo from '../repos/shopping';
import { getUserSettings, listCategories, listCategoryKeys } from '../repos/spending';
import { runCommand, type CommandEnv } from './dispatch';

export type ShoppingCommandResult<T = object> = ({ ok: true } & T) | { ok: false; error: string };

/** Bump only if the defaults change and existing users should get the additions. */
export const SHOPPING_SEED = 'shopping.v1';

function ctx(env: CommandEnv): BuildCtx {
  return { now: monotonicNow(), tz: env.tz, newId };
}

async function commit(
  env: CommandEnv,
  mutations: Mutation[],
  event?: DomainEvent,
  toast?: { headline: string; amountText: string },
): Promise<void> {
  if (mutations.length === 0) return;
  await runCommand(env, {
    mutations,
    ...(event ? { event } : {}),
    ...(toast
      ? {
          toast: {
            headline: toast.headline,
            details: { shopping: toast.amountText, spending: toast.amountText },
          },
        }
      : {}),
  });
}

/** The last price paid for an item name, for the default estimate. */
async function lastPrice(env: CommandEnv, name: string) {
  const { points } = await repo.loadPriceData(env.db, env.userId);
  const list = groupByItem(points).get(normalizeItemName(name));
  return list ? list[list.length - 1]! : null;
}

/** Adds an item; a blank estimate defaults to the last price paid (spec §6). */
export async function addItem(
  env: CommandEnv,
  input: ItemInput,
): Promise<ShoppingCommandResult<{ id: string; estimateMinor: number | null }>> {
  let estimateMinor = input.estimateMinor ?? null;
  if (estimateMinor === null && input.name.trim() !== '') {
    estimateMinor = defaultEstimate(await lastPrice(env, input.name), {
      quantity: input.quantity ?? null,
      unit: input.unit ?? null,
    });
  }
  const existing = await repo.listOpenItems(env.db, env.userId);
  const r = buildAddItem({ ...input, estimateMinor }, existing, ctx(env));
  if (!r.ok) return r;
  await commit(env, [r.mutation]);
  return { ok: true, id: r.itemId, estimateMinor };
}

export async function editItem(
  env: CommandEnv,
  id: string,
  input: ItemInput,
): Promise<ShoppingCommandResult> {
  const before = await repo.getItem(env.db, env.userId, id);
  if (!before || before.deletedAt) return { ok: false, error: 'not_found' };
  const r = buildEditItem(before, input, ctx(env));
  if (!r.ok) return r;
  if (r.mutation) await commit(env, [r.mutation]);
  return { ok: true };
}

export async function removeItem(env: CommandEnv, id: string): Promise<ShoppingCommandResult> {
  await commit(env, [buildRemoveItem(id, ctx(env))]);
  return { ok: true };
}

export async function restoreItem(env: CommandEnv, id: string): Promise<ShoppingCommandResult> {
  await commit(env, [buildRestoreItem(id, ctx(env))]);
  return { ok: true };
}

export interface BuyRequest {
  itemIds: readonly string[];
  totalMinor: number;
  prices?: Readonly<Record<string, number | null | undefined>>;
  store?: string | null;
  accountId?: string | null;
  /** v0.7.6: a card picked under "From" ('card:<payee id>'). */
  method?: string | null;
}

/**
 * Buying (spec §6): one trip, and through the shopping.expense rule one Spending expense
 * (Groceries) that budget.deduct takes out of a fund like any other. Without Spending the trip
 * is recorded with no expense.
 */
export async function buy(
  env: CommandEnv,
  req: BuyRequest,
): Promise<ShoppingCommandResult<{ tripId: string }>> {
  const c = ctx(env);
  const items = await repo.getItems(env.db, env.userId, req.itemIds);
  if (items.length !== req.itemIds.length) return { ok: false, error: 'item_not_open' };
  const r = buildBuy(
    {
      items,
      totalMinor: req.totalMinor,
      ...(req.prices ? { prices: req.prices } : {}),
      store: req.store ?? null,
      date: localDate(c.now, env.tz),
      // The expense id goes on the trip insert now; the shopping.expense rule writes that expense.
      expenseId: env.usable('spending') ? newId() : null,
      // v0.5: a picked "From" account goes on the event; otherwise the rule picks the default.
      ...(req.accountId ? { accountId: req.accountId } : {}),
      // v0.7.6: a card instead: the expense charges the card and no account.
      ...(req.method ? { method: req.method } : {}),
    },
    c,
  );
  if (!r.ok) return r;
  await commit(env, r.mutations, r.event, {
    headline: 'Bought',
    amountText: formatPeso(req.totalMinor),
  });
  return { ok: true, tripId: r.tripId };
}

/** Undo right after buying: the trip and its expense go, and the items return to the list. */
export async function undoBuy(env: CommandEnv, tripId: string): Promise<ShoppingCommandResult> {
  const trip = await repo.getTrip(env.db, env.userId, tripId);
  if (!trip) return { ok: false, error: 'not_found' };
  const lines = await repo.listTripLines(env.db, env.userId, tripId);
  await commit(env, buildUndoBuy(trip, lines, ctx(env)));
  return { ok: true };
}

/** Deletes a past trip with its lines and its expense (soft; kept in History). */
export async function deleteTrip(env: CommandEnv, tripId: string): Promise<ShoppingCommandResult> {
  const trip = await repo.getTrip(env.db, env.userId, tripId);
  if (!trip || trip.deletedAt) return { ok: false, error: 'not_found' };
  const lines = await repo.listTripLines(env.db, env.userId, tripId);
  await commit(env, buildDeleteTrip(trip, lines, ctx(env)));
  return { ok: true };
}

export async function restoreTrip(env: CommandEnv, tripId: string): Promise<ShoppingCommandResult> {
  const trip = await repo.getTrip(env.db, env.userId, tripId);
  if (!trip) return { ok: false, error: 'not_found' };
  const lines = await repo.listTripLines(env.db, env.userId, tripId);
  await commit(env, buildRestoreTrip(trip, lines, ctx(env)));
  return { ok: true };
}

/**
 * Runs whenever Shopping becomes usable: seeds the Groceries category (spec §6; not part of
 * Spending's default set) with a seeded id, insert-if-absent, once per user.
 */
export async function seedShoppingDefaults(env: CommandEnv): Promise<void> {
  const { db, userId } = env;
  const settings = await getUserSettings(db, userId);
  if (settings?.seeds[SHOPPING_SEED]) return;
  const c = ctx(env);
  const keys = await listCategoryKeys(db, userId);
  const position =
    (await listCategories(db, userId)).reduce((max, cat) => Math.max(max, cat.position), 0) + 1;
  const seeds = { ...settings?.seeds, [SHOPPING_SEED]: c.now };
  const settingsId = singletonId(userId, 'user_settings');
  const marker: Mutation = settings
    ? {
        id: newId(),
        table: 'user_settings',
        op: 'update',
        rowId: settingsId,
        values: { seeds },
        at: c.now,
        source: 'shopping',
      }
    : {
        id: newId(),
        table: 'user_settings',
        op: 'insert',
        rowId: settingsId,
        values: { weekStart: 1, seeds },
        at: c.now,
        source: 'shopping',
        ifAbsent: true,
      };
  await commit(env, [...buildSeedGroceries(userId, keys, position, c), marker]);
}
